import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { Component, computed, inject, input, output } from '@angular/core';
import { IconName, NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArchiveX,
  lucideBookOpenText,
  lucideContactRound,
  lucideFlag,
  lucideLayoutDashboard,
  lucideListTodo,
  lucideNotebookPen,
  lucideRoute,
  lucideTicketCheck,
} from '@ng-icons/lucide';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmTooltipTrigger } from '@spartan-ng/helm/tooltip';

import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { EstVerrouilleService } from '@shared/services/est-verrouille.service';
import { UserStore } from '../../../auth/store/user.store';
import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { FinParcours } from '../../models/fin-parcours.model';
import { Parcours } from '../../models/parcours.model';
import { ParcoursStateService, ProfondeurActuelle } from '../../services/parcours-state.service';
import { FinParcoursStore } from '../../store/fin-parcours/fin-parcours.store';
import { LegendeItemComponent } from '../legende-item.component';
import { ParcoursDepartComponent } from '../parcours-depart/parcours-depart.component';
import { ParcoursFinComponent } from '../parcours-fin/parcours-fin.component';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';

export type TypeNoeud = 'Jalon' | 'Etape' | 'Point-Depart' | 'Module' | 'Fin-Parcours' | 'Sequence';

type OutilWithoutIcon = Omit<Outil, 'icon'>;

export type Outil = {
  nodeType: TypeNoeud;
  niveau: ProfondeurActuelle;
  icon: IconName;
  titre: string;
  isDisabled?: boolean;
  disableMessage?: string;
};

const OUTILS_NOEUD: Outil[] = [
  {
    nodeType: 'Point-Depart',
    niveau: '01',
    icon: 'lucideTicketCheck',
    titre: 'Point de départ',
  },
  //// {
  ////   nodeType: 'Jalon',
  ////   niveau: '01',
  ////   icon: 'lucideFlag',
  ////   titre: 'Jalon',
  //// },
  {
    nodeType: 'Etape',
    niveau: '01',
    icon: 'lucideGrid2x2',
    titre: 'Étape',
  },
  {
    nodeType: 'Fin-Parcours',
    niveau: '01',
    icon: 'lucideContactRound',
    titre: 'Fin de parcours',
  },
  {
    nodeType: 'Module',
    niveau: '02',
    icon: 'lucideLayoutDashboard',
    titre: 'Module',
  },
];

@Component({
  selector: 'app-barre-d-outil',
  imports: [
    LegendeItemComponent,
    NgIcon,
    HlmButton,
    HlmTooltipTrigger,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    ParcoursDepartComponent,
    ParcoursFinComponent,
  ],
  providers: [
    provideIcons({
      lucideNotebookPen,
      lucideTicketCheck,
      lucideLayoutDashboard,
      lucideFlag,
      lucideContactRound,
      lucideBookOpenText,
      lucideListTodo,
      lucideArchiveX,
      lucideRoute,
    }),
  ],
  templateUrl: './barre-d-outil.component.html',
  styleUrl: './barre-d-outil.component.scss',
})
export class BarreDOutilComponent {
  readonly #parcoursStateService = inject(ParcoursStateService);
  readonly #finParcoursStore = inject(FinParcoursStore);
  readonly userStore = inject(UserStore);
  readonly #estVerrouille = inject(EstVerrouilleService);

  readonly typeElementSelectionne = input<string | null>(null);
  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();
  readonly parcours = input<Parcours | null>(null);
  readonly estCanvasElementsSupprimes = input.required<boolean>();

  readonly onCreateNode = output<OutilWithoutIcon>();
  readonly onCreateFinParcours = output<FinParcours>();
  readonly onToggleValidationParcours = output();
  readonly onOpenDetails = output();
  readonly onToggleCanvasElementSupprimes = output();

  protected readonly profondeurActuelle = this.#parcoursStateService.profondeurActuelle;
  protected readonly finsParcours = computed(() => this.#finParcoursStore.entities());
  protected readonly estVerrouille = computed(() =>
    this.#estVerrouille.check(
      LivrablePrincipal.PARCOURS_CROISEMENT,
      this.cartographie(),
      this.referentiel(),
      this.parcours()?.id,
    ),
  );

  protected readonly tools = computed(() => {
    const niveau = this.profondeurActuelle();
    const outilsDansNiveauActuelle = OUTILS_NOEUD.filter((tool) => tool.niveau === niveau);

    return outilsDansNiveauActuelle.map((tool) => ({
      ...tool,
      ...this.#handleToolDisabled(tool, this.typeElementSelectionne()),
    }));
  });

  isPointDepartOpen = false;
  isFinParcoursOpen = false;
  isSequenceOpen = false;

  protected onClickHandler(tool: OutilWithoutIcon) {
    this.onCreateNode.emit(tool);
  }

  protected onClickFinParcours(finParcours: FinParcours) {
    this.onCreateFinParcours.emit(finParcours);
  }

  protected isFinParcoursDisabled() {
    return this.finsParcours().length < 1;
  }

  #handleToolDisabled(tool: Outil, typeSelectionne: string | null) {
    if (!typeSelectionne) {
      return null;
    }

    switch (tool.nodeType) {
      case 'Module':
        if (typeSelectionne !== 'étape') {
          return {
            isDisabled: true,
            disableMessage: 'Vous devez sélectionner une étape pour ajouter un module.',
          };
        }
        break;
      case 'Etape':
        if (typeSelectionne !== 'jalon' && typeSelectionne !== 'étape') {
          return {
            isDisabled: true,
            disableMessage: 'Vous devez sélectionner une étape ou un jalon pour ajouter une étape.',
          };
        }
        break;
      default:
        break;
    }
    return null;
  }
}
